import React, { useState, useEffect } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export interface BannerConfig {
  id: string;
  imageUrl: string;
  redirectUrl: string;
  altText: string;
  isActive: boolean;
  startDate?: string;
  endDate?: string;
  priority?: number;
}

interface PromotionalBannerProps {
  banners: BannerConfig[];
  onDismiss?: (bannerId: string) => void;
  className?: string;
  autoSlide?: boolean;
  slideInterval?: number; // in milliseconds
}

export const PromotionalBanner: React.FC<PromotionalBannerProps> = ({
  banners,
  onDismiss,
  className = "",
  autoSlide = true,
  slideInterval = 5000
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-slide functionality
  useEffect(() => {
    if (!autoSlide || banners.length <= 1 || isPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % banners.length);
    }, slideInterval);

    return () => clearInterval(interval);
  }, [autoSlide, banners.length, slideInterval, isPaused]);

  const currentBanner = banners[currentIndex];

  const handleBannerClick = () => {
    if (currentBanner.redirectUrl) {
      // Check if it's an internal route (starts with /)
      if (currentBanner.redirectUrl.startsWith("/")) {
        // Navigate in same tab for internal routes
        window.location.href = currentBanner.redirectUrl;
      } else {
        // Open in new tab for external links
        window.open(currentBanner.redirectUrl, "_blank", "noopener,noreferrer");
      }
    }
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDismiss) {
      onDismiss(currentBanner.id);
    }
  };

  const goToPrevious = () => {
    setCurrentIndex(
      (prevIndex) => (prevIndex - 1 + banners.length) % banners.length
    );
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % banners.length);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  // Check if any banner should be active based on date range
  const getActiveBanners = () => {
    return banners.filter((banner) => {
      if (!banner.isActive) return false;

      const now = new Date();

      if (banner.startDate && new Date(banner.startDate) > now) return false;
      if (banner.endDate && new Date(banner.endDate) < now) return false;

      return true;
    });
  };

  const activeBanners = getActiveBanners();

  if (activeBanners.length === 0) {
    return null;
  }

  return (
    <div className={`relative w-full ${className}`}>
      <div
        className="relative cursor-pointer group transition-all duration-200 hover:scale-[1.01] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 overflow-hidden"
        onClick={handleBannerClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleBannerClick();
          }
        }}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        aria-label={`Click to visit ${currentBanner.altText}`}
      >
        {/* Banner Image */}
        <img
          src={currentBanner.imageUrl}
          alt={currentBanner.altText}
          className="w-full h-auto object-contain max-h-32 sm:max-h-40 md:max-h-48 lg:max-h-56"
          loading="lazy"
          onError={(e) => {
            console.warn(
              `Failed to load banner image: ${currentBanner.imageUrl}`
            );
            // Hide the banner if image fails to load
            (e.target as HTMLElement).style.display = "none";
          }}
        />

        {/* Navigation Arrows - Only show if multiple banners */}
        {activeBanners.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
              className="absolute left-2 top-1/2 transform -translate-y-1/2 p-1.5 bg-black bg-opacity-20 hover:bg-opacity-30 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-1"
              aria-label="Previous banner"
              type="button"
            >
              <ChevronLeft className="w-4 h-4 text-white" />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1.5 bg-black bg-opacity-20 hover:bg-opacity-30 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-1"
              aria-label="Next banner"
              type="button"
            >
              <ChevronRight className="w-4 h-4 text-white" />
            </button>
          </>
        )}

        {/* Dismiss button */}
        {onDismiss && (
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 p-1.5 bg-black bg-opacity-20 hover:bg-opacity-30 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-1"
            aria-label="Dismiss banner"
            type="button"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        )}

        {/* Dot Indicators - Only show if multiple banners */}
        {activeBanners.length > 1 && (
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
            {activeBanners.map((_, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  goToSlide(index);
                }}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentIndex
                    ? "bg-white bg-opacity-80"
                    : "bg-white bg-opacity-40 hover:bg-opacity-60"
                }`}
                aria-label={`Go to banner ${index + 1}`}
                type="button"
              />
            ))}
          </div>
        )}

        {/* Hover overlay for better UX */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-5 transition-opacity duration-200 pointer-events-none" />
      </div>
    </div>
  );
};

export default PromotionalBanner;
